import { useEffect, useState, useRef } from "react";


import {
  AssessmentRole,
  DeleteUserInput,
  DeleteUserInvitationInput,
  Institution,
  Program,
  ProgramMembership,
  ProgramRole,
  SearchUsersQueryVariables,
  SearchableIDFilterInput,
  SearchableSortDirection,
  SearchableStringFilterInput,
  SearchableUserFilterInput,
  SearchableUserSortInput,
  SearchableUserSortableFields,
  UpdateUserEmailArgs,
  UpdateUserInput,
  User,
  UserInvitation,
  UserStats
} from "../../API";
import { BulkOperationsSummary, QueryStatus } from "../../app/types";
import {
  deleteUserInvitation,
  fetchUserInvitationByEmail
} from "../UserInvitations/userInvitationsAPI";
import {
  fetchUser,
  updateUser as updateUserAPI,
  createUser as createUserAPI,
  deleteUser as deleteUserAPI,
  updateUserEmail as updateUserEmailAPI,
  fetchAllUsers,
  fetchUsersByEmail,
  fetchUsersByNPI,
  searchUsers,
  fetchUsersByProgramIds,
  fetchUserStats,
  AvailableParticipant,
  availableParticipantsAPI,
  AvailableParticipantsInput,
  CreateUserInput,
  CreateUserResult
} from "./usersAPI";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid";
import { UserNetworkErrors } from "../../app/constants";
import { ProgramUser } from "../../custom-types";
import { useAppDispatch } from "../../app/hooks";
import type { AppDispatch } from "../../app/store";
import {
  setShowToast,
  setToastMessage,
  setToastStyle
} from "../../common-ui/toastSlice";

export const useUserById = (id?: string) => {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getUserById = async (id: string): Promise<User | null> => {
    setStatus("loading");
    setError(null);

    try {
      const result = await fetchUser(id);
      setUser(result);
      setStatus("succeeded");
      return result;
    } catch (error) {
      console.error("error fetching user:", error);
      setStatus("failed");
      setError(`Error loading user: ${error}`);
    }
    return null;
  };

  useEffect(() => {
    if (id) {
      getUserById(id);
    }
  }, [id]);

  return {
    user,
    status,
    error,
    getUserById
  };
};

export const useDeleteUser = () => {
  const [deletedUser, setDeletedUser] = useState<User | null>(null);
  const [deletedUserInvitation, setDeletedUserInvitation] =
    useState<UserInvitation | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const deleteUser = async (user: User) => {
    setStatus("loading");
    setError(null);

    try {
      // Retrieve the user's invitation
      const invitation = await fetchUserInvitationByEmail(user.email);
      // Delete the invitation if it exists
      if (invitation) {
        const deleteUserInvitationInput: DeleteUserInvitationInput = {
          id: invitation.id
        };
        const invitationDeletionResults = await deleteUserInvitation(
          deleteUserInvitationInput
        );
        if (invitationDeletionResults) {
          console.log(
            "user's invitation successfully deleted:",
            invitationDeletionResults
          );
        }
        setDeletedUserInvitation(invitationDeletionResults);
      }
      // Delete the user from dynamoDB
      const deleteUserInput: DeleteUserInput = {
        id: user.id
      };
      const userDeletionResults = await deleteUserAPI(deleteUserInput);
      if (userDeletionResults) {
        console.log(
          "user deleted from dynamodb succesfully:",
          userDeletionResults
        );
      }
      setDeletedUser(userDeletionResults);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `${error}`;
      console.error("error in useDeleteUser:", error);
      setStatus("failed");
      setError(errorString);
    }
  };
  return {
    deletedUser,
    deletedUserInvitation,
    status,
    error,
    deleteUser
  };
};

export const useAllUsers = () => {
  const [users, setUsers] = useState<User[] | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getAllUsers = async () => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchAllUsers();
      setUsers(results);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `${error}`;
      console.error("error fetching all users:", error);
      setStatus("failed");
      setError(errorString);
    }
  };

  useEffect(() => {
    getAllUsers();
  }, []);

  return {
    users,
    status,
    error
  };
};

export const useGeneralUserSearch = (searchTerm: string) => {
  const [users, setUsers] = useState<(User | null)[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");

  const getUsers = async (searchTerm: string) => {
    setStatus("loading");
    try {
      const predicate: SearchUsersQueryVariables = {
        filter: {
          or: [
            { email: { matchPhrase: searchTerm } },
            { firstName: { matchPhrase: searchTerm } },
            { lastName: { matchPhrase: searchTerm } }
          ]
        }
      };

      if (!isNaN(parseInt(searchTerm))) {
        predicate.filter?.or?.push({ npi: { eq: parseInt(searchTerm) } });
      }

      const results = await searchUsers(predicate);
      setUsers(results?.items ?? []);
      setStatus("succeeded");
    } catch (e) {
      setStatus("failed");
    }
  };

  useEffect(() => {
    if (searchTerm.length > 3) {
      getUsers(searchTerm);
    }
  }, [searchTerm]);

  return {
    status,
    users
  };
};

// DEV NOTE: This monster will probably eventually have to be made re-usable somehow. 😅
export const useOpenSearchUsers = (
  limit: number,
  sortModel: GridSortModel,
  filterModel: GridFilterModel,
  institutions: Institution[],
  programs: Program[],
  refetchUsers: number,
  // Top selections to be combined as AND constraints
  selectedProgramId?: string,
  selectedRole?: ProgramRole | string,
  selectedStatus?: string,
  disabled: boolean = false
) => {
  const [users, setUsers] = useState<User[]>([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);

  // The token that was used to get the current page. Starts undefined.
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>(
    undefined
  );

  // The next token that came back with the current result. Used to get next page.
  const [nextToken, setNextToken] = useState("");

  // A stack of tokens to pop off of when going back. When it reaches the 'undefined' entry, it's back at the beginning.
  const [prevNextTokens, setPrevNextTokens] = useState<(string | undefined)[]>(
    []
  );
  const [sort, setSort] = useState<SearchableUserSortInput[]>([
    {
      direction: SearchableSortDirection.asc,
      field: SearchableUserSortableFields.firstName
    }
  ]);

  const [filter, setFilter] = useState<SearchableUserFilterInput>({});
  
  // Track if initial load has happened to prevent multiple calls on mount
  // Use a combination of ref and state to handle both re-renders and remounts
  const hasInitializedRef = useRef(false);
  const initializationPromiseRef = useRef<Promise<void> | null>(null);
  const prevSortRef = useRef<SearchableUserSortInput[] | null>(null);
  const prevFilterRef = useRef<SearchableUserFilterInput | null>(null);

  // When toggling between disabled (client mode) and enabled (server mode),
  // reset pagination tokens so the server list starts at page 1.
  useEffect(() => {
    if (!disabled) {
      setPrevNextTokens([]);
      setCurrentPageToken(undefined);
      setNextToken("");
      hasInitializedRef.current = false; // Reset initialization flag when re-enabling
      isInitializingRef.current = false; // Reset initializing flag when re-enabling
      initializationPromiseRef.current = null; // Clear any pending initialization promise
    }
  }, [disabled]);

  const nextPage = () => {
    if (disabled) return;
    // Add the token that was used to fetch this page to the stack.
    // The first one will be empty so that going back to page 1 will
    // do the initial fetch.
    const newPrevNextTokens = [...prevNextTokens, currentPageToken];

    // Set the state
    setPrevNextTokens(newPrevNextTokens);
    setCurrentPageToken(nextToken);

    // Fetch from server
    getBatch(nextToken);
  };

  const prevPage = () => {
    if (disabled) return;
    // Make a non-modifying clone of the state.
    const clonedPrevTokens = [...prevNextTokens];

    // Grab the last entry/modify the cloned array.
    const prevToken = clonedPrevTokens.pop();

    // Set state
    setPrevNextTokens(clonedPrevTokens);
    setCurrentPageToken(prevToken);

    // Fetch from server
    getBatch(prevToken);
  };

  const handleSortChange = (newSortModel: GridSortModel) => {
    // Use the grid-provided field verbatim; backend maps to SQL safely.
    const sortObject = newSortModel[0];
    const dir =
      sortObject?.sort === "desc"
        ? SearchableSortDirection.desc
        : SearchableSortDirection.asc;
    const fieldAny = (sortObject?.field || "firstName") as any;
    setSort([{ direction: dir, field: fieldAny } as any]);
  };

  const handleFilterChange = (newFilterModel: GridFilterModel) => {
    const itemClauses: any[] = [];
    const items = newFilterModel.items || [];
    const op =
      (newFilterModel as any)?.logicOperator ||
      (newFilterModel as any)?.linkOperator ||
      "and";

    for (const it of items) {
      const val = it?.value;
      const field = String(it?.field || "");
      if (val === undefined || val === null || val === "" || !field) continue;
      const contains = { contains: String(val) } as any;

      switch (field) {
        case "firstName":
          itemClauses.push({ firstName: contains });
          break;
        case "lastName":
          itemClauses.push({ lastName: contains });
          break;
        case "email":
          itemClauses.push({ email: contains });
          break;
        case "institutionName":
          itemClauses.push({ institution: contains });
          break;
        case "program":
          itemClauses.push({ program: contains });
          break;
        case "role":
          itemClauses.push({ role: contains });
          break;
        case "trainingProgram":
          itemClauses.push({ trainingProgram: contains });
          break;
        case "status":
          itemClauses.push({ status: contains });
          break;
        case "jobTitle":
          itemClauses.push({ jobTitle: contains });
          break;
        case "npi":
          itemClauses.push({ npi: { eq: parseInt(String(val), 10) } });
          break;
        case "pgy":
          itemClauses.push({ pgy: { eq: parseInt(String(val), 10) } });
          break;
        default:
          break;
      }
    }

    // Top selections as mandatory AND constraints
    const topClauses: any[] = [];
    if (selectedProgramId) {
      topClauses.push({ programId: { eq: selectedProgramId } });
    }
    if (selectedRole && String(selectedRole).length > 0) {
      topClauses.push({ role: { contains: String(selectedRole) } });
    }
    if (selectedStatus && String(selectedStatus).length > 0) {
      topClauses.push({ status: { contains: String(selectedStatus) } });
    }

    // Build final filter with AND at root, and grid items grouped by operator
    const finalFilter: any = { and: [] as any[] };
    if (topClauses.length > 0) {
      finalFilter.and.push(...topClauses);
    }
    if (itemClauses.length > 0) {
      if (String(op).toLowerCase() === "or") {
        finalFilter.and.push({ or: itemClauses });
      } else {
        finalFilter.and.push(...itemClauses);
      }
    }
    if (finalFilter.and.length === 0) {
      setFilter({});
    } else {
      setFilter(finalFilter);
    }
  };

  /**
   * Grab a batch of Users from the backend using sort, filter, limit, and page tokens.
   * @param getPageToken - Token to use to fetch the desired page.
   */
  const getBatch = async (getPageToken?: string) => {
    if (disabled) return;
    
    setStatus("loading");
    setError("");

    try {
      const predicate: SearchUsersQueryVariables = {
        sort: sort,
        filter: filter,
        limit: limit,
        nextToken: getPageToken
      };

      const results = await searchUsers(predicate);

      // Filter out any null items from the array
      // (this likely won't happen, but TS thinks it might)
      const returnedUsers = results?.items.flatMap((item) =>
        item ? [item] : []
      );

      // If the list has items, set it.
      if (returnedUsers) {
        setUsers(returnedUsers);
      }

      // Utilize the OpenSearch count/total results.
      const totalResults = results?.total;
      if (totalResults) {
        setTotal(totalResults);
      }

      // If there's a nextToken included in the results of this batch,
      // set it in the state.
      const returnedNextToken = results?.nextToken;
      if (returnedNextToken) {
        setNextToken(returnedNextToken);
      }
      setStatus("succeeded");
    } catch (error) {
      console.error("error searching users:", error);
      const errorString = `error searching users: ${error}`;
      setStatus("failed");
      setError(errorString);
    }
  };

  // Initial load: set up sort and filter state directly, then load once
  // Use a ref to track if we've started initialization to prevent multiple simultaneous calls
  const isInitializingRef = useRef(false);
  
  useEffect(() => {
    if (disabled || hasInitializedRef.current || isInitializingRef.current || initializationPromiseRef.current) {
      return;
    }
    
    // Mark as initializing to prevent concurrent calls
    isInitializingRef.current = true;
    
    // Set initial sort state directly (don't use handler to avoid triggering other useEffects)
    const sortObject = sortModel[0];
    const dir =
      sortObject?.sort === "desc"
        ? SearchableSortDirection.desc
        : SearchableSortDirection.asc;
    const fieldAny = (sortObject?.field || "firstName") as any;
    const initialSort = [{ direction: dir, field: fieldAny } as any];
    
    // Set initial filter state directly
    const itemClauses: any[] = [];
    const items = filterModel.items || [];
    const op =
      (filterModel as any)?.logicOperator ||
      (filterModel as any)?.linkOperator ||
      "and";

    for (const it of items) {
      const val = it?.value;
      const field = String(it?.field || "");
      if (val === undefined || val === null || val === "" || !field) continue;
      const contains = { contains: String(val) } as any;

      switch (field) {
        case "firstName":
          itemClauses.push({ firstName: contains });
          break;
        case "lastName":
          itemClauses.push({ lastName: contains });
          break;
        case "email":
          itemClauses.push({ email: contains });
          break;
        case "institutionName":
          itemClauses.push({ institution: contains });
          break;
        case "program":
          itemClauses.push({ program: contains });
          break;
        case "role":
          itemClauses.push({ role: contains });
          break;
        case "trainingProgram":
          itemClauses.push({ trainingProgram: contains });
          break;
        case "status":
          itemClauses.push({ status: contains });
          break;
        case "jobTitle":
          itemClauses.push({ jobTitle: contains });
          break;
        case "npi":
          itemClauses.push({ npi: { eq: parseInt(String(val), 10) } });
          break;
        case "pgy":
          itemClauses.push({ pgy: { eq: parseInt(String(val), 10) } });
          break;
        default:
          break;
      }
    }

    // Top selections as mandatory AND constraints
    const topClauses: any[] = [];
    if (selectedProgramId) {
      topClauses.push({ programId: { eq: selectedProgramId } });
    }
    if (selectedRole && String(selectedRole).length > 0) {
      topClauses.push({ role: { contains: String(selectedRole) } });
    }
    if (selectedStatus && String(selectedStatus).length > 0) {
      topClauses.push({ status: { contains: String(selectedStatus) } });
    }

    // Build final filter with AND at root, and grid items grouped by operator
    const finalFilter: any = { and: [] as any[] };
    if (topClauses.length > 0) {
      finalFilter.and.push(...topClauses);
    }
    if (itemClauses.length > 0) {
      if (String(op).toLowerCase() === "or") {
        finalFilter.and.push({ or: itemClauses });
      } else {
        finalFilter.and.push(...itemClauses);
      }
    }
    const initialFilter = finalFilter.and.length === 0 ? {} : finalFilter;
    
    // Store initial values in refs BEFORE doing anything to prevent other effects from firing
    prevSortRef.current = initialSort;
    prevFilterRef.current = initialFilter;
    hasInitializedRef.current = true;
    
    // Set state synchronously - the guards in other effects will prevent them from firing
    setSort(initialSort);
    setFilter(initialFilter);
    
    // Load once - call API directly with initial values
    const predicate: SearchUsersQueryVariables = {
      sort: initialSort,
      filter: initialFilter,
      limit: limit,
      nextToken: undefined
    };
    
    // Call the API directly
    const initPromise = (async () => {
      try {
        setStatus("loading");
        setError("");
        const results = await searchUsers(predicate);
        const returnedUsers = results?.items.flatMap((item) =>
          item ? [item] : []
        );
        if (returnedUsers) {
          setUsers(returnedUsers);
        }
        const totalResults = results?.total;
        if (totalResults) {
          setTotal(totalResults);
        }
        const returnedNextToken = results?.nextToken;
        if (returnedNextToken) {
          setNextToken(returnedNextToken);
        }
        setStatus("succeeded");
        // Mark initialization as complete after API call
        isInitializingRef.current = false;
        initializationPromiseRef.current = null;
      } catch (error) {
        console.error("error searching users:", error);
        const errorString = `error searching users: ${error}`;
        setStatus("failed");
        setError(errorString);
        // Mark initialization as complete even on error
        isInitializingRef.current = false;
        initializationPromiseRef.current = null;
      }
    })();
    
    // Store the promise in ref
    initializationPromiseRef.current = initPromise;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  // If the limit is changed for the DataGrid component, get the first
  // batch again with the new limit in place.
  // Use a ref to track previous limit to avoid firing on initial mount
  const prevLimitRef = useRef<number | null>(null);
  useEffect(() => {
    if (disabled || !hasInitializedRef.current || isInitializingRef.current) {
      // Store initial limit but don't process it
      if (prevLimitRef.current === null) {
        prevLimitRef.current = limit;
      }
      return;
    }
    // Only process if limit actually changed
    if (prevLimitRef.current !== limit) {
      prevLimitRef.current = limit;
      getBatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, disabled]);

  // If the sortModel changes from DataGrid, perform the logic that
  // sets the sort state.
  // Use a ref to track the previous sortModel to avoid firing on initial mount
  const prevSortModelRef = useRef<GridSortModel | null>(null);
  useEffect(() => {
    if (disabled || !hasInitializedRef.current) {
      // Store initial sortModel but don't process it
      if (!prevSortModelRef.current) {
        prevSortModelRef.current = sortModel;
      }
      return;
    }
    // Only process if sortModel actually changed
    const sortModelChanged = JSON.stringify(prevSortModelRef.current) !== JSON.stringify(sortModel);
    if (sortModelChanged) {
      prevSortModelRef.current = sortModel;
      handleSortChange(sortModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortModel, disabled]);

  // After the sort state has updated, get the first batch.
  // Skip if this is the initial value (prevSortRef is null) or if we're still initializing
  useEffect(() => {
    if (disabled || !hasInitializedRef.current || isInitializingRef.current || prevSortRef.current === null) {
      return;
    }
    const sortChanged = JSON.stringify(prevSortRef.current) !== JSON.stringify(sort);
    if (sortChanged) {
      getBatch();
      prevSortRef.current = sort;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, disabled]);

  // If the filterModel changes from DataGrid, perform the logic that
  // sets the filter state.
  // Use refs to track previous values to avoid firing on initial mount
  const prevFilterModelRef = useRef<GridFilterModel | null>(null);
  const prevSelectedProgramIdRef = useRef<string | undefined>(selectedProgramId);
  const prevSelectedRoleRef = useRef<ProgramRole | string | undefined>(selectedRole);
  const prevSelectedStatusRef = useRef<string | undefined>(selectedStatus);
  
  useEffect(() => {
    if (disabled || !hasInitializedRef.current || isInitializingRef.current) {
      // Store initial values but don't process them
      if (!prevFilterModelRef.current) {
        prevFilterModelRef.current = filterModel;
        prevSelectedProgramIdRef.current = selectedProgramId;
        prevSelectedRoleRef.current = selectedRole;
        prevSelectedStatusRef.current = selectedStatus;
      }
      return;
    }
    
    // Only process if something actually changed
    const filterModelChanged = JSON.stringify(prevFilterModelRef.current) !== JSON.stringify(filterModel);
    const programIdChanged = prevSelectedProgramIdRef.current !== selectedProgramId;
    const roleChanged = prevSelectedRoleRef.current !== selectedRole;
    const statusChanged = prevSelectedStatusRef.current !== selectedStatus;
    
    if (filterModelChanged || programIdChanged || roleChanged || statusChanged) {
      prevFilterModelRef.current = filterModel;
      prevSelectedProgramIdRef.current = selectedProgramId;
      prevSelectedRoleRef.current = selectedRole;
      prevSelectedStatusRef.current = selectedStatus;
      handleFilterChange(filterModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModel, disabled, selectedProgramId, selectedRole, selectedStatus]);

  // After the filter state has updated, get the first batch.
  // Skip if this is the initial value (prevFilterRef is null) or if we're still initializing
  useEffect(() => {
    if (disabled || !hasInitializedRef.current || isInitializingRef.current || prevFilterRef.current === null) {
      return;
    }
    const filterChanged = JSON.stringify(prevFilterRef.current) !== JSON.stringify(filter);
    if (filterChanged) {
      getBatch();
      prevFilterRef.current = filter;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, disabled]);

  // After a refetch signal is sent, trigger a refetch
  // Only refetch if refetchUsers actually changes (not on initial mount)
  const prevRefetchUsersRef = useRef<number>(refetchUsers);
  useEffect(() => {
    if (disabled || !hasInitializedRef.current || isInitializingRef.current) {
      // Store initial refetchUsers but don't process it
      if (prevRefetchUsersRef.current !== refetchUsers) {
        prevRefetchUsersRef.current = refetchUsers;
      }
      return;
    }
    // Only process if refetchUsers actually changed
    if (prevRefetchUsersRef.current !== refetchUsers) {
      prevRefetchUsersRef.current = refetchUsers;
      getBatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchUsers, disabled]);

  return {
    users,
    status,
    error,
    total,
    nextToken,
    nextPage,
    prevPage
  };
};

export const useUsersByProgramIds = () => {
  const [users, setUsers] = useState<ProgramUser[] | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [lastRequestParams, setLastRequestParams] = useState<{
    programIds: string[];
    role?: ProgramRole;
    activeUsersOnly: boolean;
  } | null>(null);

  const getUsersByProgramIds = async (
    programIds: string[],
    role?: ProgramRole,
    activeUsersOnly = false
  ): Promise<User[] | null> => {
    if (programIds.length === 0) {
      return null;
    }

    // Prevent duplicate calls with same parameters
    const sortedIds = [...programIds].sort().join(",");
    const lastSortedIds = lastRequestParams?.programIds.sort().join(",") || "";
    const sameParams =
      sortedIds === lastSortedIds &&
      lastRequestParams?.role === role &&
      lastRequestParams?.activeUsersOnly === activeUsersOnly;

    if (status === "loading" || (sameParams && status === "succeeded")) {
      return users;
    }

    setStatus("loading");
    setError(null);
    setLastRequestParams({ programIds, role, activeUsersOnly });

    try {
      const usersInProgram = await fetchUsersByProgramIds(
        programIds,
        role,
        activeUsersOnly
      );
      setUsers(usersInProgram);
      setStatus("succeeded");
      return usersInProgram;
    } catch (error) {
      console.error("error in useUsersByProgramIds:", error);
      const errorString = `error in useUsersByProgramIds: ${error}`;
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  return {
    users,
    status,
    error,
    getUsersByProgramIds
  };
};

export const useUsersByEmail = (email?: string) => {
  const [users, setUsers] = useState<User[] | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getUsersByEmail = async (email: string): Promise<User[] | null> => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchUsersByEmail(email);
      setUsers(results);
      setStatus("succeeded");
      return results;
    } catch (error) {
      console.error("error in useUsersByEmail:", error);
      const errorString = `${error}`;
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  useEffect(() => {
    if (email) {
      getUsersByEmail(email);
    }
  }, [email]);

  return {
    users,
    status,
    error,
    getUsersByEmail
  };
};

export const useUsersByNPI = (npi?: number) => {
  const [users, setUsers] = useState<User[] | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getUsersByNPI = async (npi: number): Promise<User[] | null> => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchUsersByNPI(npi);
      setUsers(results);
      setStatus("succeeded");
      return results;
    } catch (error) {
      console.error("error in useUsersByNPI:", error);
      const errorString = `${error}`;
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  useEffect(() => {
    if (npi) {
      getUsersByNPI(npi);
    }
  }, [npi]);

  return {
    users,
    status,
    error,
    getUsersByNPI
  };
};

export const useUserStats = (id?: string) => {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getStatsByUserId = async (id: string): Promise<UserStats | null> => {
    setStatus("loading");
    setError(null);

    try {
      const result = await fetchUserStats(id);
      setStats(result);
      setStatus("succeeded");
      return result;
    } catch (error) {
      console.error("error getting stats:", error);
      setStatus("failed");
      const msg =
        error instanceof Error
          ? error.message
          : typeof error === "object" && error !== null
            ? JSON.stringify(error)
            : String(error);
      setError(`Error fetching user stats: ${msg}`);
    }
    return null;
  };

  useEffect(() => {
    if (id) {
      getStatsByUserId(id);
    }
  }, [id]);

  return {
    stats,
    status,
    error,
    getStatsByUserId
  };
};

export const useUpdateUser = () => {
  const [updatedUser, setUpdatedUser] = useState<User | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const updateUser = async (input: UpdateUserInput) => {
    setStatus("loading");
    setError(null);

    try {
      const updateResult = await updateUserAPI(input);
      setUpdatedUser(updateResult);
      setStatus("succeeded");
      return updateResult;
    } catch (caughtError) {
      const errorInstance = caughtError as Error;
      const errorMessage = errorInstance?.message || "Error updating user";
      console.error("Error updating user:", caughtError);
      setStatus("failed");
      setError(errorMessage);
      return null;
    }
  };

  return {
    updatedUser,
    status,
    error,
    updateUser
  };
};

export const useCreateUser = () => {
  const [newUser, setNewUser] = useState<CreateUserResult | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const createUser = async (input: CreateUserInput) => {
    setStatus("loading");
    setError(null);

    try {
      const creationResult = await createUserAPI(input);
      if (!creationResult?.success) {
        const message =
          creationResult?.error || UserNetworkErrors.CreateUserError;
        setStatus("failed");
        setError(message);
        return null;
      }
      setNewUser(creationResult);
      setStatus("succeeded");
      return creationResult;
    } catch (error) {
      const errorString = `Error creating user: ${JSON.stringify(error)}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  return {
    newUser,
    status,
    error,
    createUser
  };
};

export const useAvailableParticipants = () => {
  const [availableParticipants, setAvailableParticipants] = useState<
    AvailableParticipant[]
  >([]);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const getAvailableParticipants = async (
    input: AvailableParticipantsInput
  ) => {
    setStatus("loading");
    setError(null);

    try {
      const results = await availableParticipantsAPI(input);
      setAvailableParticipants(results);
      setStatus("succeeded");
    } catch (error) {
      console.error("error fetching available participants:", error);
      const errorString = JSON.stringify(error);
      setStatus("failed");
      setError(errorString);
    }
  };

  return {
    availableParticipants,
    status,
    error,
    getAvailableParticipants
  };
};

export const useUpdateUserEmail = () => {
  const [updateEmailResult, setUpdateEmailResult] = useState<string | null>(
    null
  );
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const updateUserEmail = async (input: UpdateUserEmailArgs) => {
    setStatus("loading");
    setError(null);

    try {
      const updateResult = await updateUserEmailAPI(input);
      setUpdateEmailResult(updateResult);
      setStatus("succeeded");
      return updateResult;
    } catch (error) {
      const errorString = `Error updating user email: ${error}`;
      console.error(errorString);
      setStatus("failed");
      const anyError = error as any;
      // Prefer server-sent error message if available (e.g., 409 conflict message)
      const serverMessage =
        (anyError?.response &&
          (anyError.response.data?.error || anyError.response.data?.message)) ||
        undefined;
      const thrownMessage = typeof anyError?.message === "string" ? anyError.message : undefined;
      const finalMessage = serverMessage || thrownMessage || UserNetworkErrors.UpdateEmailError;
      setError(finalMessage);
    }
    return null;
  };

  return {
    updateEmailResult,
    status,
    error,
    updateUserEmail
  };
};

export const returnUsersAdminedProgramMemberships = (
  user: User
): ProgramMembership[] => {
  const toReturn: ProgramMembership[] = [];
  const usersProgramMemberships = user.programMemberships?.items;
  if (usersProgramMemberships) {
    for (const programMembership of usersProgramMemberships) {
      if (
        programMembership &&
        programMembership.programRole === ProgramRole.ProgramAdmin
      ) {
        toReturn.push(programMembership);
      }
    }
  }
  return toReturn;
};

export interface FilterUserByProgramAdminProps {
  users: User[];
  currentUser: User;
}

export const filterUsersByProgramAdmin = ({
  users,
  currentUser
}: FilterUserByProgramAdminProps): User[] => {
  // Get the list of programs that the current user an admin of.
  const programMembershipsWhereCurrentUserIsAdmin =
    returnUsersAdminedProgramMemberships(currentUser);

  // Filter the full user list to those that are either:
  // - a trainee
  // - an attending or
  // - a fellow program admin
  // of any program that the currently logged in user is a program admin of.
  return (
    users?.filter((user) => {
      const usersProgramMemberships = user.programMemberships?.items;
      // First, make sure that the currently signed in user has ProgramAdmin roles
      // and the user-in-question has program memberships.
      if (
        usersProgramMemberships &&
        usersProgramMemberships.length > 0 &&
        programMembershipsWhereCurrentUserIsAdmin &&
        programMembershipsWhereCurrentUserIsAdmin.length > 0
      ) {
        // Create a list of overlapping memberships where the signed in user is a ProgramAdmin
        // and the user-in-question is ProgramAdmin or User (Trainee or Attending) of the same program.
        const sharedProgramMemberships = usersProgramMemberships.filter(
          (usersProgramMembership) => {
            for (const programMembershipWhereCurrentUserIsAdmin of programMembershipsWhereCurrentUserIsAdmin) {
              if (
                programMembershipWhereCurrentUserIsAdmin?.programId ===
                  usersProgramMembership?.programId &&
                usersProgramMembership?.programRole &&
                [
                  ProgramRole.Trainee,
                  ProgramRole.Attending,
                  ProgramRole.ProgramAdmin
                ].includes(usersProgramMembership.programRole)
              ) {
                return true;
              }
            }
          }
        );
        // If there are any overalapping memberships, this user should be
        // displayed in the list.
        if (sharedProgramMemberships.length > 0) {
          return true;
        }
      }
    }) ?? []
  );
};

export const UserNotifications = {
  NoUsers: "No users to display.",
  NoAssociatedInstitution: "No associated Institution.",
  EmailUpdatedSuccessfully: "Email updated successfully.",
  UserUpdatedSuccessfully: "User updated successfully."
};

export enum CognitoUserGroups {
  Users = "Users",
  ProgramAdmins = "ProgramAdmins",
  InstitutionalAdmins = "InstitutionalAdmins",
  SysAdmins = "SysAdmins"
}

export const showToastForBulkResult = (
  dispatch: AppDispatch,
  bulkResults: BulkOperationsSummary,
  totalItemsCount: number,
  successMessage: string,
  partialSuccessMessage: string,
  errorMessage: string
) => {
  const { fulfilled, rejected } = bulkResults;

  if (totalItemsCount === fulfilled) {
    dispatch(setToastStyle("success"));
    dispatch(setToastMessage(successMessage));
  } else if (totalItemsCount === rejected) {
    dispatch(setToastStyle("error"));
    dispatch(setToastMessage(errorMessage));
  } else {
    // this condition will also get triggered for users that for some reason have no associated cognito username
    dispatch(setToastStyle("warning"));
    dispatch(setToastMessage(partialSuccessMessage));
  }
  dispatch(setShowToast(true));
};
